import { effect } from '@angular/core';

/**
 * Shows the browser's native "leave site?" prompt on tab close/refresh while
 * `hasUnsavedChanges()` is true. Complements `CanDeactivate`, which only
 * guards in-app router navigation, not browser-level unload. Must be called
 * from an injection context (constructor or field initializer).
 *
 * Treat this as a progressive enhancement, not a guaranteed safeguard —
 * `beforeunload` is not on the Baseline "Widely available" list and has
 * several long-standing limitations:
 * - The confirmation text is always a generic, browser-controlled string;
 *   no custom message can be shown. Setting `event.returnValue` to a custom
 *   string is a legacy no-op kept here only for older-engine compatibility —
 *   `event.preventDefault()` is what current browsers actually key off.
 * - The handler must run synchronously; there is no way to await an async
 *   check before the prompt appears.
 * - Some browsers only show the prompt after the user has interacted with
 *   the page at least once ("sticky activation").
 * - Behavior on mobile browsers (backgrounding/OS-killing a tab) is
 *   inconsistent and the event may not fire at all.
 * - Merely having a listener registered can disable the back/forward cache
 *   (bfcache) in some browsers, which is why the listener here is
 *   attached/detached reactively via `effect()` instead of unconditionally.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event | MDN: Window: beforeunload event}
 * @see {@link https://stackoverflow.com/questions/47331260/how-to-prevent-page-refresh-in-angular-4 | Stack Overflow: How to prevent page refresh in Angular}
 * @see {@link https://medium.com/@altamashali/angular-candeactivate-guard-e9069e1adf0f | Combining CanDeactivate with a beforeunload listener}
 */
export function warnBeforeUnload(hasUnsavedChanges: () => boolean): void {
    const listener = (event: BeforeUnloadEvent) => {
        event.preventDefault();

        // Included for legacy support, e.g. Chrome/Edge < 119
        event.returnValue = true;
    };

    // onCleanup runs both when the condition flips back and when the effect is destroyed, so it
    // covers teardown on its own - no separate DestroyRef hook needed.
    effect((onCleanup) => {
        if (!hasUnsavedChanges()) {
            return;
        }

        window.addEventListener('beforeunload', listener);
        onCleanup(() => window.removeEventListener('beforeunload', listener));
    });
}
